import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { historyApi, type HistoryEntry } from '@/api/history';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { LedNumber } from '@/components/LedNumber';
import { PhotoModal } from '@/components/PhotoModal';
import { SignPanel } from '@/components/SignPanel';
import { useSession } from '@/state/session';
import { formatDollars } from '@/lib/payouts';
import { cn } from '@/lib/utils';

export function HistoryDetail() {
  const { id } = useParams<{ id: string }>();
  const tid = Number(id);
  const user = useSession((s) => s.user);
  const isAdminElevated = !!(user?.isAdmin && user.sessionElevated);

  const q = useQuery({
    queryKey: ['history', 'detail', tid],
    queryFn: () => historyApi.detail(tid),
    enabled: Number.isInteger(tid),
  });

  const [photoEntry, setPhotoEntry] = useState<HistoryEntry | null>(null);

  if (!Number.isInteger(tid)) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-flame-red">Invalid tournament id.</p>
      </main>
    );
  }
  if (q.isLoading) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }
  if (!q.data) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-flame-red">Couldn't load tournament.</p>
      </main>
    );
  }

  const { tournament, entries, payouts, potCents } = q.data;
  const canViewAnyPhoto = (e: HistoryEntry) =>
    e.hasStartingPhoto || e.hasEndingPhoto;
  const myEntry = (e: HistoryEntry) => user && e.userId === user.id;

  return (
    <main className="mx-auto max-w-2xl px-5 py-6 space-y-5">
      <Breadcrumbs
        crumbs={[
          { label: 'History', to: '/history' },
          { label: tournament.name },
        ]}
      />

      <header>
        <p className="font-display uppercase tracking-widest text-cabinet-gold-bright text-xs">
          {tournament.machineName}
        </p>
        <SignPanel size="md" className="mt-1">
          <h1 className="text-marquee text-2xl uppercase leading-none">
            {tournament.name}
          </h1>
        </SignPanel>
        <p className="text-xs text-cabinet-cream/50 mt-1">
          Closed{' '}
          {tournament.completedAt
            ? new Date(tournament.completedAt).toLocaleString()
            : '—'}
        </p>
      </header>

      <section className="cabinet-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
            Final payouts
          </h2>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
              Pot
            </p>
            <LedNumber value={Math.round(potCents / 100)} currency digits={5} size="md" />
          </div>
        </div>
        <ul className="space-y-2">
          {payouts.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <span className="font-display text-cabinet-gold-bright text-xl w-10 text-center">
                  {placeLabel(p.place)}
                </span>
                <span className="text-cabinet-cream">
                  {p.userDisplayName ?? `User ${p.userId}`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-cabinet-cream tabular-nums">
                  {formatDollars(p.amountCents)}
                </span>
                <span
                  className={cn(
                    'text-[10px] uppercase tracking-widest',
                    p.paid ? 'text-cabinet-gold-bright' : 'text-cabinet-flame-orange',
                  )}
                >
                  {p.paid ? 'Paid' : 'Unpaid'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="cabinet-card p-5 space-y-3">
        <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
          Entries ({entries.length})
        </h2>
        <ul className="space-y-2">
          {entries.map((e) => {
            const tappable =
              canViewAnyPhoto(e) && (isAdminElevated || myEntry(e));
            const Wrapper: React.ElementType = tappable ? 'button' : 'div';
            return (
              <li key={e.id}>
                <Wrapper
                  type={tappable ? 'button' : undefined}
                  onClick={tappable ? () => setPhotoEntry(e) : undefined}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2 text-left',
                    tappable && 'hover:ring-cabinet-gold/40 transition cursor-pointer min-h-[44px]',
                    e.isTiebreaker && 'ring-cabinet-flame-orange/30 bg-cabinet-flame-red/5',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-cabinet-cream truncate">
                      {e.userDisplayName ?? `User ${e.userId}`}
                      {e.isTiebreaker && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-flame-orange">
                          Tiebreaker
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-cabinet-cream/60 tabular-nums">
                      {e.startingBalance !== null && e.endingBalance !== null
                        ? `${e.startingBalance} → ${e.endingBalance}`
                        : 'no score'}
                      {tappable && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-cream/40">
                          · tap for photos
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-led tabular-nums text-cabinet-led-on text-lg">
                      {e.score ?? '—'}
                    </p>
                  </div>
                </Wrapper>
              </li>
            );
          })}
        </ul>
      </section>

      <Link
        to="/history"
        className="block text-center text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
      >
        ← Back to history
      </Link>

      {photoEntry && (
        <PhotoModal
          entryId={photoEntry.id}
          title={photoEntry.userDisplayName ?? `Entry`}
          startingBalance={photoEntry.startingBalance}
          endingBalance={photoEntry.endingBalance}
          hasStartingPhoto={photoEntry.hasStartingPhoto}
          hasEndingPhoto={photoEntry.hasEndingPhoto}
          onClose={() => setPhotoEntry(null)}
        />
      )}
    </main>
  );
}

function placeLabel(p: number): string {
  const v = p % 100;
  if (v >= 11 && v <= 13) return `${p}th`;
  switch (p % 10) {
    case 1:
      return `${p}st`;
    case 2:
      return `${p}nd`;
    case 3:
      return `${p}rd`;
    default:
      return `${p}th`;
  }
}
